import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { usePublicVisitorInfo } from '@/hooks/usePublicVisitorInfo';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { getLucideIcon } from '@/utils/iconUtils';
import FooterLogo from '@/components/FooterLogo';
import type { VisitorInfoSection } from '@/types/visitorInfo';
const VisitorInfo = () => {
  const { t, language } = useLanguage();
  const { visitorInfo, loading, error } = usePublicVisitorInfo();

  const getColorClasses = (color: string) => {
    const colorMap = {
      red: 'bg-museum-shapes-red/20 text-museum-shapes-red',
      blue: 'bg-museum-shapes-blue/20 text-museum-shapes-blue',
      green: 'bg-museum-shapes-green/20 text-museum-shapes-green',
      purple: 'bg-museum-shapes-purple/20 text-museum-shapes-purple',
      yellow: 'bg-museum-shapes-yellow/20 text-museum-shapes-yellow',
      orange: 'bg-museum-shapes-orange/20 text-museum-shapes-orange',
      pink: 'bg-museum-shapes-pink/20 text-museum-shapes-pink',
      indigo: 'bg-museum-shapes-indigo/20 text-museum-shapes-indigo',
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  const renderSectionContent = (section: VisitorInfoSection) => {
    const content = section.content;

    switch (section.type) {
      case 'opening_hours':
        const hours = content[language] || content.en || [];
        return (
          <ul className="space-y-2 text-gray-600 pl-12 pt-4">
            {hours.map((hour: string, index: number) => (
              <li key={index} className="list-disc text-lg">{hour}</li>
            ))}
          </ul>
        );

      case 'contact':
        const PhoneIcon = getLucideIcon('phone');
        const MailIcon = getLucideIcon('mail');
        return (
          <div className="space-y-3 pl-12 pt-4">
            {content.phone && (
              <a href={`tel:${content.phone}`} className="flex items-center gap-2 text-museum hover:text-museum-dark transition-colors">
                <PhoneIcon className="h-5 w-5" />
                <span className="font-medium text-lg">{content.phone}</span>
              </a>
            )}
            {content.email && (
              <a href={`mailto:${content.email}`} className="flex items-center gap-2 text-museum hover:text-museum-dark transition-colors">
                <MailIcon className="h-5 w-5" />
                <span className="font-medium text-lg">{content.email}</span>
              </a>
            )}
          </div>
        );

      case 'address':
        const address = content[language] || content.en || '';
        return (
          <div className="p-4 pt-4 bg-gradient-to-br from-museum-light/30 to-white">
            <p className="text-gray-600 text-lg">{address}</p>
          </div>
        );

      case 'links':
        const links = content[language] || content.en || [];
        const LinkIcon = getLucideIcon('link');
        return (
          <ul className="space-y-3 pl-12 pt-4">
            {links.map((link: { title: string; url: string }, index: number) => (
              <li key={index}>
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-museum hover:underline hover:text-museum-dark transition-colors">
                  <LinkIcon className="h-4 w-4" />
                  <span className="font-medium text-lg">{link.title}</span>
                </a>
              </li>
            ))}
          </ul>
        );

      case 'custom':
      default:
        const customContent = content[language] || content.en || '';
        return (
          <div className="p-4 pt-4 bg-gradient-to-br from-museum-light/30 to-white">
            <p className="text-gray-600 whitespace-pre-wrap text-lg">{customContent}</p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-8 relative">
        <div className="agam-shape agam-shape-2"></div>
        <div className="agam-shape agam-shape-4"></div>
        
        <div className="bg-gradient-to-r from-museum-shapes-yellow/30 to-museum-shapes-green/20 py-4 px-6 rounded-xl mb-6">
          <Skeleton className="h-8 w-48" />
        </div>
        
        <div className="bg-white rounded-xl shadow-md overflow-hidden relative z-10">
          <div className="space-y-4 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !visitorInfo) {
    return (
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-8 relative">
        <div className="agam-shape agam-shape-2"></div>
        <div className="agam-shape agam-shape-4"></div>
        
        
        <h1 className="section-title mb-6 after:hidden font-bold">{t('visitor_info')}</h1>
        
        <div className="bg-white rounded-xl shadow-md overflow-hidden relative z-10 p-6">
          <p className="text-center text-muted-foreground">
            {error ? 'Error loading visitor information' : 'No visitor information available'}
          </p>
        </div>
      </div>
    );
  }
  return <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-8 relative">
      {/* Abstract shapes in background */}
      <div className="agam-shape agam-shape-2"></div>
      <div className="agam-shape agam-shape-4"></div>
      
      
      <h1 className="section-title mb-6 after:hidden text-center font-bold">{t('visitor_info')}</h1>
      
      <div className="bg-white rounded-xl shadow-md overflow-hidden relative z-10">
        <Accordion type="single" collapsible className="mb-0">
          {visitorInfo.sections
            .filter(section => section.enabled)
            .sort((a, b) => a.order - b.order)
            .map((section, index) => {
              const IconComponent = getLucideIcon(section.icon);
              const isLast = index === visitorInfo.sections.filter(s => s.enabled).length - 1;
              
              return (
                <AccordionItem key={section.id} value={section.id} className={isLast ? "border-b-0" : ""}>
                  <AccordionTrigger className="flex items-center gap-2 p-4 hover:bg-museum-light/50">
                    <div className={`p-2 rounded-full ${getColorClasses(section.color)}`}>
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <span className="font-display font-bold text-xl">
                      {section.title[language] || section.title.en || 'Untitled'}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="p-4 pt-0 bg-gradient-to-br from-museum-light/30 to-white">
                    {renderSectionContent(section)}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
        </Accordion>
      </div>
      
      <FooterLogo />
    </div>;
};
export default VisitorInfo;